import { useState, useEffect } from "react";

import { Mail, AlertTriangle, CheckCircle2, Loader2, Lock, Trash2, AlertCircle, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { StepUpAuthDialog } from "@/components/security/StepUpAuthDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useStepUpAuth } from "@/hooks/useStepUpAuth";
import { supabase } from "@/integrations/supabase/client";
import { ROUTES } from "@/lib/routes";
import { emailSchema, checkEmailTypos, validateEmailDomainDNS } from "@/lib/validation";
import { authClient } from "@/lib/auth-client";

export const ProfileSettings = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { requireStepUp, showStepUpDialog, setShowStepUpDialog, currentActionType, stepUpData } = useStepUpAuth();
  
  const [email, setEmail] = useState(user?.email || "");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [typoSuggestion, setTypoSuggestion] = useState<string | null>(null);
  const [validatingDNS, setValidatingDNS] = useState(false);
  
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [recentTakeout, setRecentTakeout] = useState<any>(null);
  const [checkingTakeout, setCheckingTakeout] = useState(true);

  useEffect(() => {
    const checkRecentTakeout = async () => {
      try {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const { data } = await supabase
          .from('takeout_requests')
          .select('*')
          .eq('status', 'completed')
          .gte('completed_at', thirtyDaysAgo.toISOString())
          .order('completed_at', { ascending: false })
          .limit(1)
          .single();

        setRecentTakeout(data);
      } catch (error) {
        console.error('Error checking takeout:', error);
      } finally {
        setCheckingTakeout(false);
      }
    };

    checkRecentTakeout();
  }, []);

  const validateEmail = async (emailValue: string) => {
    setValidationError(null);
    setTypoSuggestion(null);

    // Basic format validation
    try {
      emailSchema.parse(emailValue);
    } catch (error) {
      if (error instanceof z.ZodError) {
        setValidationError(error.issues[0].message);
        return false;
      }
    }

    // Check for common typos
    const suggestion = checkEmailTypos(emailValue);
    if (suggestion) {
      setTypoSuggestion(`Did you mean ${suggestion}?`);
    }

    // DNS validation - check if domain has valid MX records
    setValidatingDNS(true);
    try {
      const dnsValidation = await validateEmailDomainDNS(emailValue);
      if (!dnsValidation.isValid) {
        setValidationError(dnsValidation.message || "This email domain cannot receive mail");
        return false;
      }
    } finally {
      setValidatingDNS(false);
    }

    return true;
  };

  const handleSaveEmail = async () => {
    if (!email || email === user?.email) {
      setIsEditing(false);
      return;
    }

    const isValid = await validateEmail(email);
    if (!isValid) return;

    // Require step-up authentication for email change
    await requireStepUp('change_email', async () => {
      setSaving(true);

      try {
        const { error } = await authClient.changeEmail({
          newEmail: email,
          callbackURL: `${window.location.origin}/profile`,
        });

        if (error) throw error;

        toast.success("Verification email sent to your new address.");
        setIsEditing(false);
      } catch (error: any) {
        console.error("Error updating email:", error);
        toast.error(error.message || "Failed to update email");
        setEmail(user?.email || "");
      } finally {
        setSaving(false);
      }
    });
  };

  const handleSavePassword = async () => {
    if (!newPassword || newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    if (!currentPassword) {
      toast.error("Please enter your current password");
      return;
    }

    // Require step-up authentication for password change
    await requireStepUp('change_password', async () => {
      setSavingPassword(true);

      try {
        const { error } = await authClient.changePassword({
          newPassword: newPassword,
          currentPassword: currentPassword,
          revokeOtherSessions: true,
        });

        if (error) throw error;

        toast.success("Password updated successfully!");
        setIsEditingPassword(false);
        setNewPassword("");
        setConfirmPassword("");
        setCurrentPassword("");
      } catch (error: any) {
        console.error("Error updating password:", error);
        toast.error(error.message || "Failed to update password");
      } finally {
        setSavingPassword(false);
      }
    });
  };

  const handleDeleteAccount = async () => {
    // Require step-up authentication for account deletion
    await requireStepUp('delete_account', async () => {
      setDeleting(true);

      try {
        const { error } = await authClient.deleteUser();
        
        if (error) throw error;

        toast.success("Account deleted successfully");
        await signOut();
      } catch (error: any) {
        console.error("Error deleting account:", error);
        toast.error(error.message || "Failed to delete account. Please contact support.");
      } finally {
        setDeleting(false);
        setShowDeleteDialog(false);
      }
    });
  };

  const handleCancel = () => {
    setEmail(user?.email || "");
    setIsEditing(false);
    setValidationError(null);
    setTypoSuggestion(null);
  };

  const handleApplySuggestion = () => {
    if (typoSuggestion) {
      const suggested = typoSuggestion.match(/([^\s]+@[^\s?]+)/)?.[0];
      if (suggested) {
        setEmail(suggested);
        setTypoSuggestion(null);
        setValidationError(null);
      }
    }
  };

  return (
    <>
      <div className="space-y-6">
        {/* Email Change Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Address
            </CardTitle>
            <CardDescription>
              Update your email address for login and notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="flex gap-2">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setValidationError(null);
                    setTypoSuggestion(null);
                  }}
                  disabled={!isEditing || saving}
                  className="flex-1"
                  placeholder="your.email@example.com"
                />
                {!isEditing ? (
                  <Button
                    onClick={() => setIsEditing(true)}
                    variant="outline"
                  >
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={handleSaveEmail}
                      disabled={saving || validatingDNS || !email || email === user?.email}
                    >
                      {(saving || validatingDNS) ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {validatingDNS ? "Validating..." : "Saving..."}
                        </>
                      ) : (
                        "Save"
                      )}
                    </Button>
                    <Button
                      onClick={handleCancel}
                      variant="outline"
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>

            {validationError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}

            {typoSuggestion && !validationError && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <span>{typoSuggestion}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleApplySuggestion}
                  >
                    Use suggestion
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {user?.emailVerified && !isEditing && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  Your email address is verified and active.
                </AlertDescription>
              </Alert>
            )}

            <div className="text-sm text-muted-foreground space-y-1">
              <p className="font-medium">Email validation includes:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Valid email format verification</li>
                <li>Common typo detection (e.g., gmial → gmail)</li>
                <li>Disposable email blocking</li>
                <li>DNS validation (checks if domain can receive mail)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Password Change Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Password
            </CardTitle>
            <CardDescription>
              Change your password to keep your account secure
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isEditingPassword ? (
              <Button
                onClick={() => setIsEditingPassword(true)}
                variant="outline"
              >
                Change Password
              </Button>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={savingPassword}
                    placeholder="Enter current password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={savingPassword}
                    placeholder="Enter new password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={savingPassword}
                    placeholder="Confirm new password"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSavePassword}
                    disabled={savingPassword || !newPassword || !confirmPassword || !currentPassword}
                  >
                    {savingPassword ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Password"
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      setIsEditingPassword(false);
                      setNewPassword("");
                      setConfirmPassword("");
                      setCurrentPassword("");
                    }}
                    variant="outline"
                    disabled={savingPassword}
                  >
                    Cancel
                  </Button>
                </div>
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Password must be at least 8 characters long
                  </AlertDescription>
                </Alert>
              </>
            )}
          </CardContent>
        </Card>

        {/* Account Deletion Section */}
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete Account
            </CardTitle>
            <CardDescription>
              Permanently delete your account and all associated data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This action cannot be undone. All your data will be permanently deleted and cannot be recovered.
              </AlertDescription>
            </Alert>

            {!checkingTakeout && !recentTakeout && (
              <Alert>
                <Download className="h-4 w-4" />
                <AlertDescription className="space-y-2">
                  <p className="font-medium">⚠️ No recent data export found</p>
                  <p>
                    Before deleting your account, we strongly recommend exporting your data.
                    Once deleted, we cannot recover your account or data.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(ROUTES.TAKEOUT)}
                    className="mt-2"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export My Data First
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {!checkingTakeout && recentTakeout && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium">Recent data export found</p>
                  <p className="text-sm text-muted-foreground">
                    Last export: {new Date(recentTakeout.completed_at).toLocaleDateString()}
                  </p>
                </AlertDescription>
              </Alert>
            )}

            <Button
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
              disabled={deleting}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Account
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Step-Up Authentication Dialog */}
      <StepUpAuthDialog
        open={showStepUpDialog}
        onOpenChange={setShowStepUpDialog}
        onVerified={() => setShowStepUpDialog(false)}
        actionType={currentActionType}
        reason={stepUpData?.reason}
        anomalyScore={stepUpData?.anomaly_score}
      />

      {/* Account Deletion Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your account and remove all your data from our servers.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {deleting ? "Deleting..." : "Delete Account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
